import { useEffect, useRef, useState } from 'react';
import { useDeal } from '../../hooks/useDeal';
import { useLens } from '../../hooks/useLens';
import { useConversationStore } from '../../store/conversation';
import { useDealsStore } from '../../store/deals';
import { claudeApi } from '../../services/api';
import { LENS_LABELS, LENS_GEAR_LETTER, LENS_VALUE_TEXT } from '../../lib/lens';
import { renderInlineMd, uid, nowIso, formatTime } from '../../lib/utils';
import type { ConversationMessage, Lens } from '../../types';

interface Props { dealId: string; }

export function RightColumn({ dealId }: Props) {
  const { deal, data, conversation, isStreaming, streamingDraft } = useDeal(dealId);
  const { lens, isAuto, setLens } = useLens(dealId);
  const appendMessage = useConversationStore(s => s.appendMessage);
  const appendMessageLocal = useConversationStore(s => s.appendMessageLocal);
  const setStreaming = useConversationStore(s => s.setStreaming);
  const appendStreamingDelta = useConversationStore(s => s.appendStreamingDelta);
  const resetStreamingDraft = useConversationStore(s => s.resetStreamingDraft);
  const clearThread = useConversationStore(s => s.clearThread);

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation.length, streamingDraft]);

  if (!deal) return <aside className="col-right" />;

  const onSend = async () => {
    const text = draft.trim();
    if (!text || isStreaming) return;
    setDraft('');

    const userMsg: ConversationMessage = {
      id: uid('m'), dealId, role: 'user', lensAtSend: lens,
      body: text, createdAt: nowIso(),
    };
    await appendMessage(userMsg);

    setStreaming(dealId, true);
    resetStreamingDraft(dealId);
    try {
      const result = await claudeApi.chatStream(
        dealId, text, lens, userMsg.id,
        (delta) => appendStreamingDelta(dealId, delta),
      );
      // The function persisted the message server-side; just push it into local state.
      const assistantMsg: ConversationMessage = {
        id: result.messageId,
        dealId,
        role: 'assistant',
        lensAtSend: lens,
        body: result.fullText,
        createdAt: result.createdAt,
      };
      appendMessageLocal(assistantMsg);
    } catch (err: any) {
      const errMsg: ConversationMessage = {
        id: uid('m'), dealId, role: 'system', lensAtSend: lens,
        body: `⚠ Error from Claude API: ${err?.message ?? err}`,
        createdAt: nowIso(),
      };
      await appendMessage(errMsg);
    } finally {
      resetStreamingDraft(dealId);
      setStreaming(dealId, false);
    }
  };

  const onRegen = async () => {
    if (isStreaming) return;
    // Find the last user message
    const lastUser = [...conversation].reverse().find(m => m.role === 'user');
    if (!lastUser) return;
    // Remove the most recent assistant message (we'll generate a fresh one)
    // Note: we don't actually delete it from the server — the new exchange just appends.
    setStreaming(dealId, true);
    resetStreamingDraft(dealId);
    try {
      const result = await claudeApi.chatStream(
        dealId,
        lastUser.body + ' (regenerate with current context)',
        lastUser.lensAtSend,
        undefined,
        (delta) => appendStreamingDelta(dealId, delta),
      );
      const assistantMsg: ConversationMessage = {
        id: result.messageId,
        dealId,
        role: 'assistant',
        lensAtSend: lastUser.lensAtSend,
        body: result.fullText,
        createdAt: result.createdAt,
      };
      appendMessageLocal(assistantMsg);
    } catch (err: any) {
      const errMsg: ConversationMessage = {
        id: uid('m'), dealId, role: 'system', lensAtSend: lens,
        body: `⚠ Regen failed: ${err?.message ?? err}`,
        createdAt: nowIso(),
      };
      await appendMessage(errMsg);
    } finally {
      resetStreamingDraft(dealId);
      setStreaming(dealId, false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSend();
    }
  };

  const onClearThread = async () => {
    if (window.confirm('Clear this deal\'s entire conversation thread? This can\'t be undone.')) {
      await clearThread(dealId);
    }
  };

  return (
    <aside className="col-right">
      <div className="right-tabs">
        <div className="right-tab active">
          Conversation <span className="badge">{conversation.length}</span>
        </div>
      </div>

      {/* LENS STRIP */}
      <div className="lens-strip">
        <div className="lens-strip-label">Lens</div>
        <div className="lens-chips">
          {(['general', 'title', 'package'] as Lens[]).map(l => (
            <button
              key={l}
              className={`lens-chip ${lens === l ? 'active' : ''}`}
              onClick={() => setLens(l === lens && !isAuto ? null : l)}
            >
              {LENS_LABELS[l]}
            </button>
          ))}
        </div>
        <span className="lens-auto-tag" title={isAuto ? 'Auto-switched based on the active center tab' : 'You overrode the lens manually'}>
          {isAuto ? '↻ auto' : '✎ manual'}
        </span>
      </div>

      {/* SCOPE BANNER */}
      <div className="convo-scope-banner">
        <span>
          <span className="scope-tag">▸ Full deal context</span>
          {' '}· {data.files.length} files · {data.findings.length} findings · {conversation.length}-msg thread
        </span>
        <button className="reset-btn" onClick={onClearThread}>Clear thread ↺</button>
      </div>

      <div className="conversation" ref={scrollRef}>
        <div className="system-prompt-pill">
          <div className="gear">{LENS_GEAR_LETTER[lens]}</div>
          <div style={{ flex: 1 }}>
            <div className="label">Lens · {LENS_LABELS[lens]}</div>
            <div className="value">{LENS_VALUE_TEXT[lens]}</div>
          </div>
          <div className="arrow">›</div>
        </div>

        {conversation.length === 0 && !isStreaming && (
          <div className="msg system">
            <div className="msg-body">No messages yet · ask Claude anything about this deal</div>
          </div>
        )}

        {conversation.map(m => <Message key={m.id} m={m} />)}

        {isStreaming && (
          <div className="msg assistant">
            <div className="msg-meta assistant">
              <span className="msg-meta-tag">Claude · {LENS_LABELS[lens]}</span>
              <span style={{ color: 'var(--text-faint)' }}>· streaming</span>
            </div>
            {streamingDraft ? (
              <div className="msg-body" dangerouslySetInnerHTML={{ __html: renderInlineMd(streamingDraft) + '<span class="cursor-blink">▌</span>' }} />
            ) : (
              <div className="msg-body" style={{ opacity: 0.6 }}>
                <span className="pulse-dot" style={{ display: 'inline-block', width: 6, height: 6, marginRight: 4 }} />
                <span className="pulse-dot" style={{ display: 'inline-block', width: 6, height: 6, marginRight: 4 }} />
                <span className="pulse-dot" style={{ display: 'inline-block', width: 6, height: 6 }} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="composer">
        <textarea
          className="composer-input"
          placeholder={
            lens === 'title'   ? 'Ask about title · request a clerk search · run the math…' :
            lens === 'package' ? 'Tweak a section, change framing, regenerate copy…' :
                                 'Ask anything about this deal…'
          }
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKey}
        />
        <div className="composer-bar">
          <div className="composer-tools">
            <button
              className="composer-tool"
              onClick={() => void onRegen()}
              disabled={isStreaming || !conversation.some(m => m.role === 'user')}
              title="Regenerate the last response with current deal context"
            >
              ↻ Regen
            </button>
          </div>
          <button className="send-btn" onClick={onSend} disabled={isStreaming || !draft.trim()}>
            {isStreaming ? 'Sending…' : 'Send ⏎'}
          </button>
        </div>
      </div>
    </aside>
  );
}

function Message({ m }: { m: ConversationMessage }) {
  if (m.role === 'system') {
    return (
      <div className="msg system">
        <div className="msg-body">{m.body}</div>
      </div>
    );
  }
  return (
    <div className={`msg ${m.role}`}>
      <div className={`msg-meta ${m.role}`}>
        <span className="msg-meta-tag">
          {m.role === 'user' ? 'You' : `Claude · ${LENS_LABELS[m.lensAtSend]}`}
        </span>
        <span>· {formatTime(m.createdAt)}</span>
      </div>
      {m.role === 'user' ? (
        <div className="msg-body">{m.body}</div>
      ) : (
        <div className="msg-body" dangerouslySetInnerHTML={{ __html: renderInlineMd(m.body) }} />
      )}
      {m.toolEvents && m.toolEvents.length > 0 && (
        <div className="msg-tools">
          {m.toolEvents.map((t, i) => (
            <span key={i} className={`tool-pill ${t.kind}`}>
              <span className="dot" />{t.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
